import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select, { MultiValue } from 'react-select';
import {
    add_announcements,
    delete_announcements,
    get_all_announcements_last_five,
    update_announcements,
    get_active_departments_api, // Real API function
    get_active_locations_api,   // Real API function
} from '../api/widgetApi'; // Adjust path if needed
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AnnouncementsTypes, AnnouncementCriterion } from '../../../../app/modules/accounts/components/settings/SettingsModel'; // Adjust path if needed
import { KTIcon } from '../../../helpers'; // Adjust path if needed
import { permissionCheck } from '../../../../app/helper-functions/PermissionCheck'; // Adjust path if needed
import { get_all_active_locations } from '../../../../app/pages/Settings/Checklist-Exit-Interview/api/checklistExitInterviewApi';

// Permission variables
let employee_announcement_add: boolean = false;
let employee_announcement_edit: boolean = false;
let employee_announcement_log: boolean = false;
let employee_announcement_delete: boolean = false;

interface SelectOptionType {
    value: number;
    label: string;
}

// Helper to transform API data to react-select options
const transformToOptions = (apiData: Array<{ id: number; name?: string; title?: string;[key: string]: any }>): SelectOptionType[] => {
    if (!Array.isArray(apiData)) {
        console.error("transformToOptions expected an array, received:", apiData);
        return [];
    }
    return apiData.map(item => {
        // **ADJUST 'item.name' or 'item.title' if your API uses a different property for the label**
        const label = item.name || item.title || `Item ${item.id}`;
        return { value: item.id, label: label };
    });
};


function Announcements() {
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<AnnouncementsTypes | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [titleErrors, settitleErrors] = useState("");
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [selectedModalDepartments, setSelectedModalDepartments] = useState<MultiValue<SelectOptionType>>([]);
    const [selectedModalLocations, setSelectedModalLocations] = useState<MultiValue<SelectOptionType>>([]);

    useEffect(() => {
        employee_announcement_add = permissionCheck('announcement-create');
        employee_announcement_edit = permissionCheck('announcement-update');
        employee_announcement_delete = permissionCheck('announcement-delete');
        employee_announcement_log = permissionCheck('announcement-activity-log');
    }, []);

    const {
        data: announcementsApiResponse,
        isLoading: isLoadingAnnouncements,
        isError: isErrorAnnouncements,
    } = useQuery('allAnnouncements', get_all_announcements_last_five, {
        onError: (err: any) => {
            toast.error(err.message || 'Failed to fetch announcements');
        }
    });
    // Assuming get_all_announcements_last_five returns { success: true, data: [...], message: "..." }
    const AnnouncementsList: AnnouncementsTypes[] = announcementsApiResponse?.data || [];
 // Department query with refetchOnWindowFocus set to false
// Department query configuration
const {
    data: departmentOptions = [],
    isLoading: isLoadingDepartments,
    refetch: refetchDepartments, // Get the refetch function
} = useQuery('departments', async () => {
    const response = await get_active_departments_api();
    return transformToOptions(response.data);
}, {
    enabled: false, // <-- Prevents fetching on mount
    refetchOnWindowFocus: false, // <-- Don't refetch just because window gets focus
    staleTime: 0, // <-- Data is always stale, forcing network fetch on refetch()
});

// Location query configuration
const {
    data: locationOptions = [],
    isLoading: isLoadingLocations,
    refetch: refetchLocations, // Get the refetch function
} = useQuery('locations', async () => {
    const response = await get_all_active_locations();
    return transformToOptions(response.data);
}, {
    enabled: false, // <-- Prevents fetching on mount
    refetchOnWindowFocus: false, // <-- Don't refetch just because window gets focus
    staleTime: 0, // <-- Data is always stale, forcing network fetch on refetch()
});

// Handler for department dropdown opening
const handleDepartmentMenuOpen = () => {
    refetchDepartments(); // <-- Explicitly trigger fetch when menu opens
};

// Handler for location dropdown opening
const handleLocationMenuOpen = () => {
    refetchLocations(); // <-- Explicitly trigger fetch when menu opens
};

    const buildAnnouncementCriteria = (
        selectedDeptOptions: MultiValue<SelectOptionType>,
        selectedLocOptions: MultiValue<SelectOptionType>,
        // existingRawCriteria is the announcement_criteria array directly from the fetched announcement
        existingRawCriteria?: AnnouncementCriterion[]
    ): AnnouncementCriterion[] => {
        const newPayloadCriteria: AnnouncementCriterion[] = [];

        // Handle Departments
        if (selectedDeptOptions && selectedDeptOptions.length > 0) {
            const departmentIds = selectedDeptOptions.map(d => d.value);
            // Try to find an existing department criterion *object* to get its ID
            const existingDeptCriterionObject = existingRawCriteria?.find(
                c => c.subtitle === 'active-departments' // Or c.documentable_type === 'App\\Models\\Department'
            );

            const deptPayload: AnnouncementCriterion = {
                "active-departments": departmentIds,
            };
            if (existingDeptCriterionObject?.id) {
                deptPayload.id = existingDeptCriterionObject.id; // Use the ID of the criterion itself
            }
            newPayloadCriteria.push(deptPayload);
        } else {
            // If no departments are selected, but an existing department criterion was there,
            // we might need to send a signal to delete it or clear it.
            // This depends on your backend. If sending the criterion ID with an empty array clears it:
            const existingDeptCriterionObject = existingRawCriteria?.find(c => c.subtitle === 'active-departments');
            if (existingDeptCriterionObject?.id) {
                // Option 1: Backend deletes if 'active-departments' is missing or empty
                // By not adding it to newPayloadCriteria, it might be removed.

                // Option 2: Backend expects explicit clear
                // newPayloadCriteria.push({ id: existingDeptCriterionObject.id, "active-departments": [] });
            }
        }

        // Handle Locations
        if (selectedLocOptions && selectedLocOptions.length > 0) {
            const locationIds = selectedLocOptions.map(l => l.value);
            // Try to find an existing location criterion *object* to get its ID
            const existingLocCriterionObject = existingRawCriteria?.find(
                c => c.subtitle === 'active-locations' // Or c.documentable_type === 'App\\Models\\Locations'
            );

            const locPayload: AnnouncementCriterion = {
                "active-locations": locationIds,
            };
            if (existingLocCriterionObject?.id) {
                locPayload.id = existingLocCriterionObject.id; // Use the ID of the criterion itself
            }
            newPayloadCriteria.push(locPayload);
        } else {
            // Similar logic for locations if clearing is needed
            const existingLocCriterionObject = existingRawCriteria?.find(c => c.subtitle === 'active-locations');
            if (existingLocCriterionObject?.id) {
                // Handle clearing/deletion based on backend requirements
            }
        }
        return newPayloadCriteria;
    };

    const addMutation = useMutation(add_announcements, {
        onSuccess: (data) => {
            toast.success(data.message || 'Announcement added successfully!');
            queryClient.invalidateQueries('allAnnouncements');
            setShowForm(false);
            formik.resetForm();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || error.message || 'Database Error: Unable to Process the Request.');
        },
    });

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
            status: '1',
            active_departments: [] as MultiValue<SelectOptionType>,
            active_locations: [] as MultiValue<SelectOptionType>,
        },
        validationSchema: Yup.object({
            title: Yup.string().required('Title is required'),
            status: Yup.string().oneOf(['1', '0'], 'Invalid Status').required('Status is required'),
        }),
        onSubmit: (values) => {
            const announcement_criteria = buildAnnouncementCriteria(
                values.active_departments,
                values.active_locations
            );
            const submissionValues: any = {
                title: values.title,
                description: values.description,
                status: Number(values.status),
            };
            if (announcement_criteria.length > 0) {
                submissionValues.announcement_criteria = announcement_criteria;
            }
            addMutation.mutate(submissionValues);
        },
    });

    const deleteMutation = useMutation((id: number) => delete_announcements(id), {
        onSuccess: (data) => {
            toast.success(data.message || 'Announcement deleted successfully');
            queryClient.invalidateQueries('allAnnouncements');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || error.message || 'Unable to delete announcement');
        },
    });

    const updateMutation = useMutation(
        (announcementData: AnnouncementsTypes) => update_announcements(announcementData.id, announcementData),
        {
            onSuccess: (data) => {
                toast.success(data.message || 'Announcement updated successfully');
                queryClient.invalidateQueries('allAnnouncements');
                setSelectedAnnouncement(null);
            },
            onError: (error: any) => {
                toast.error(error.response?.data?.message || error.message || 'Unable to update announcement');
            },
        }
    );

    const handleAddClick = () => {
        formik.resetForm({
            values: {
                title: '',
                description: '',
                status: '1',
                active_departments: [],
                active_locations: [],
            }
        });
        setShowForm(true);
    };

    const handleEditClick = (announcement: AnnouncementsTypes) => {
        setSelectedAnnouncement(announcement);
        settitleErrors("");
        let deptsToSelect: MultiValue<SelectOptionType> = [];
        let locsToSelect: MultiValue<SelectOptionType> = [];

        if (announcement.announcement_criteria && departmentOptions.length > 0 && locationOptions.length > 0) {
            const selectedDeptIds: number[] = [];
            const selectedLocIds: number[] = [];

            announcement.announcement_criteria.forEach(criterion => {
                if (criterion.subtitle === 'active-departments' && criterion.documentable_id) {
                    selectedDeptIds.push(criterion.documentable_id);
                } else if (criterion.subtitle === 'active-locations' && criterion.documentable_id) {
                    selectedLocIds.push(criterion.documentable_id);
                }
            });

            if (selectedDeptIds.length > 0) {
                deptsToSelect = departmentOptions.filter(opt => selectedDeptIds.includes(opt.value));
            }
            if (selectedLocIds.length > 0) {
                locsToSelect = locationOptions.filter(opt => selectedLocIds.includes(opt.value));
            }
        } else if (announcement.announcement_criteria) {
            // If options are not yet loaded, we might want to retry setting this once options are available
            // For now, log a warning. This could happen if handleEditClick is called before departmentOptions/locationOptions are populated.
            console.warn("Department/Location options not yet loaded. Cannot pre-select criteria for edit modal.");
        }

        setSelectedModalDepartments(deptsToSelect);
        setSelectedModalLocations(locsToSelect);
    };

    const handleUpdateModalSubmit = () => {
        if (selectedAnnouncement && selectedAnnouncement.title.trim() !== "") {
            const criteria = buildAnnouncementCriteria(
                selectedModalDepartments,
                selectedModalLocations,
                selectedAnnouncement.announcement_criteria
            );
            const updatedData: AnnouncementsTypes = {
                ...selectedAnnouncement,
                announcement_criteria: criteria.length > 0 ? criteria : undefined,
            };
            if (updatedData.announcement_criteria && updatedData.announcement_criteria.length === 0) {
                delete updatedData.announcement_criteria;
            }
            updateMutation.mutate(updatedData);
        } else {
            settitleErrors("Title is required");
        }
    };

    const handleAnnouncementLogs = (data: AnnouncementsTypes) => {
        navigate("/dashboard/announcement-Log", { state: { id: data.id } });
    };
    const handleAllAnnouncementsLogs = () => {
        navigate("/dashboard/all-announcements-Logs");
    };

    return (
        <div className="card card-stretch mb-5 shadow-sm">
            <div className="card-header border-0 pt-5 d-flex justify-content-between align-items-center">
                <h3 className="card-title fw-bold text-dark">Announcements</h3>
                <div className='card-toolbar' data-bs-toggle='tooltip' data-bs-placement='top' data-bs-trigger='hover' title="Additional actions">
                    {employee_announcement_log && (
                        <button data-bs-toggle="tooltip" data-bs-placement="bottom" title="View All Announcements Logs"
                            className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1' onClick={handleAllAnnouncementsLogs}>
                            <i className='bi bi-journal-text'></i>
                        </button>
                    )}
                    {!showForm && employee_announcement_add && (
                        <button className="btn btn-primary" onClick={handleAddClick}>
                            <i className="fas fa-plus"></i>
                            <span className="d-none d-md-inline ms-2">Add Announcement</span>
                            <span className="d-inline d-md-none">Add</span>
                        </button>
                    )}
                    {showForm && (
                        <button className="btn btn-secondary" onClick={() => { setShowForm(false); formik.resetForm(); }}>
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            <div className="card-body pt-5">
                {showForm ? (
                    <form onSubmit={formik.handleSubmit} className="p-4 border rounded bg-light mb-4">
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label required fw-bold">Title</label>
                            <input id="title" type="text" className={`form-control ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                                {...formik.getFieldProps('title')} placeholder="Enter announcement title" />
                            {formik.touched.title && formik.errors.title && (<div className="invalid-feedback">{formik.errors.title}</div>)}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="description" className="form-label fw-bold">Description</label>
                            <textarea id="description" className="form-control" rows={4} {...formik.getFieldProps('description')}
                                placeholder="Enter announcement description" />
                        </div>
                        <div className="mb-3">
                            <label className="form-label required fw-bold">Status</label>
                            <select className={`form-select ${formik.touched.status && formik.errors.status ? 'is-invalid' : ''}`} {...formik.getFieldProps('status')}>
                                <option value="">Select a Status...</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                            {formik.touched.status && formik.errors.status && (<div className="invalid-feedback">{formik.errors.status}</div>)}
                        </div>
                        <div className="mb-3">
    <label htmlFor="active_departments" className="form-label fw-bold">Departments</label>
    <Select
        id="active_departments"
        name="active_departments"
        options={departmentOptions}
        isMulti
        isLoading={isLoadingDepartments}
        onMenuOpen={handleDepartmentMenuOpen} // <-- Link opening event to fetch handler
        onChange={(selectedOption) => formik.setFieldValue('active_departments', selectedOption)}
        value={formik.values.active_departments}
        className="basic-multi-select"
        classNamePrefix="select"
        placeholder="Select departments..."
    />
</div>

<div className="mb-3">
    <label htmlFor="active_locations" className="form-label fw-bold">Locations</label>
    <Select
        id="active_locations"
        name="active_locations"
        options={locationOptions}
        isMulti
        isLoading={isLoadingLocations}
        onMenuOpen={handleLocationMenuOpen} // <-- Link opening event to fetch handler
        onChange={(selectedOption) => formik.setFieldValue('active_locations', selectedOption)}
        value={formik.values.active_locations}
        className="basic-multi-select"
        classNamePrefix="select"
        placeholder="Select locations..."
    />
</div>

                        <button type="submit" className="btn btn-primary" disabled={addMutation.isLoading || isLoadingDepartments || isLoadingLocations}>
                            {addMutation.isLoading ? (<><span className='spinner-border spinner-border-sm align-middle me-2'></span>Posting...</>) : ('Post Announcement')}
                        </button>
                    </form>
                ) : (
                    <div>
                        {isLoadingAnnouncements && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                <p className="mt-2">Loading announcements...</p>
                            </div>
                        )}
                        {isErrorAnnouncements && (<div className="alert alert-danger text-center py-5"><p>Error fetching announcements.</p></div>)}
                        {!isLoadingAnnouncements && !isErrorAnnouncements && (
                            <ul className="list-group list-group-flush">
                                {Array.isArray(AnnouncementsList) && AnnouncementsList.length > 0 ? (
                                    AnnouncementsList.map((announcement) => (
                                        <li key={announcement.id} className="list-group-item d-flex justify-content-between align-items-center py-3">
                                            <div className="d-flex flex-column flex-grow-1 me-2">
                                                <h5 className="mb-1 fw-bold">{announcement.title}</h5>
                                                <p className="mb-0 text-muted">{announcement.description || 'No description provided.'}</p>
                                                {announcement.announcement_criteria && announcement.announcement_criteria.length > 0 && (
                                                    <small className='text-muted mt-1'>
                                                        Targeted:
                                                        {(() => { // IIFE for logic
                                                            const departmentTitles: string[] = announcement.announcement_criteria
                                                                .filter(c => c.subtitle === 'active-departments' && c.title)
                                                                .map(c => c.title!); // Use non-null assertion if sure title exists

                                                            const locationTitles: string[] = announcement.announcement_criteria
                                                                .filter(c => c.subtitle === 'active-locations' && c.title)
                                                                .map(c => c.title!);

                                                            const displayElements: JSX.Element[] = [];

                                                            if (departmentTitles.length > 0) {
                                                                displayElements.push(
                                                                    <span key="depts-target-names" className="ms-1">
                                                                        <span className="fw-semibold">Depts:</span> {departmentTitles.join(', ')}
                                                                    </span>
                                                                );
                                                            }

                                                            if (locationTitles.length > 0) {
                                                                // Add a separator if departments were also listed
                                                                if (displayElements.length > 0) {
                                                                    displayElements.push(<span key="sep-target">; </span>);
                                                                }
                                                                displayElements.push(
                                                                    <span key="locs-target-names" className={displayElements.length > 0 ? "ms-1" : ""}> {/* Add ms-1 only if it's not the first element */}
                                                                        <span className="fw-semibold">Locs:</span> {locationTitles.join(', ')}
                                                                    </span>
                                                                );
                                                            }

                                                            if (displayElements.length === 0 && announcement.announcement_criteria.length > 0) {
                                                                // Fallback if criteria exist but don't match known subtitles or have no titles
                                                                return (
                                                                    <span className="ms-1 badge bg-light text-dark">
                                                                        Other Criteria ({announcement.announcement_criteria.length})
                                                                    </span>
                                                                );
                                                            }

                                                            return displayElements;
                                                        })()}
                                                    </small>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center">
                                                {employee_announcement_log && (
                                                    <button data-bs-toggle="tooltip" data-bs-placement="bottom" title="View Announcement Logs"
                                                        className="btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1"
                                                        onClick={() => handleAnnouncementLogs(announcement)}><i className="bi bi-journal-text"></i></button>
                                                )}
                                                {employee_announcement_edit && (
                                                    <button className="btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1"
                                                        onClick={() => handleEditClick(announcement)} data-bs-toggle="modal"
                                                        data-bs-target="#editModal" data-bs-placement="bottom" title="Edit Announcement">
                                                        <KTIcon iconName="pencil" className="fs-3" /></button>
                                                )}
                                                {employee_announcement_delete && (
                                                    <button className="btn btn-icon btn-light-dark btn-active-color-danger btn-sm"
                                                        onClick={() => setSelectedAnnouncement(announcement)} data-bs-toggle="modal"
                                                        data-bs-target="#deleteModal" data-bs-placement="bottom" title="Delete Announcement">
                                                        <KTIcon iconName="trash" className="fs-3" /></button>
                                                )}
                                            </div>
                                        </li>
                                    ))
                                ) : (<li className="list-group-item text-center text-muted py-4">No announcements available</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="modal fade" id="editModal" tabIndex={-1} aria-labelledby="editModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="editModalLabel">Edit Announcement</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setSelectedAnnouncement(null)}></button>
                        </div>
                        <div className="modal-body">
                            {selectedAnnouncement && (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label required fw-bold">Title</label>
                                        <input type="text" className={`form-control ${titleErrors ? 'is-invalid' : ''}`} value={selectedAnnouncement.title}
                                            onChange={(e) => {
                                                if (e.target.value.trim() !== "") settitleErrors(""); else settitleErrors("Title is required");
                                                setSelectedAnnouncement({ ...selectedAnnouncement, title: e.target.value });
                                            }} />
                                        {titleErrors && (<div className="invalid-feedback">{titleErrors}</div>)}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Description</label>
                                        <textarea className="form-control" rows={4} value={selectedAnnouncement.description || ''}
                                            onChange={(e) => setSelectedAnnouncement({ ...selectedAnnouncement, description: e.target.value })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Status</label>
                                        <select className="form-select" value={selectedAnnouncement.status}
                                            onChange={(e) => setSelectedAnnouncement({ ...selectedAnnouncement, status: Number(e.target.value) })}>
                                            <option value={1}>Active</option>
                                            <option value={0}>Inactive</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Departments</label>
                                        <Select options={departmentOptions} isMulti isLoading={isLoadingDepartments}
                                            value={selectedModalDepartments} onChange={(selected) => setSelectedModalDepartments(selected)}
                                            className="basic-multi-select" classNamePrefix="select" placeholder="Select departments..." />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Locations</label>
                                        <Select options={locationOptions} isMulti isLoading={isLoadingLocations}
                                            value={selectedModalLocations} onChange={(selected) => setSelectedModalLocations(selected)}
                                            className="basic-multi-select" classNamePrefix="select" placeholder="Select locations..." />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" data-bs-dismiss="modal" onClick={() => setSelectedAnnouncement(null)}>Close</button>
                            <button type="button" className="btn btn-primary" onClick={handleUpdateModalSubmit}
                                disabled={!selectedAnnouncement || titleErrors !== "" || updateMutation.isLoading || isLoadingDepartments || isLoadingLocations}
                                data-bs-dismiss={!titleErrors && !updateMutation.isLoading ? "modal" : ""}>
                                {updateMutation.isLoading ? (<><span className='spinner-border spinner-border-sm align-middle me-2'></span>Updating...</>) : ('Update Announcement')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="deleteModal" tabIndex={-1} aria-labelledby="deleteModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="deleteModalLabel">Delete Announcement</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setSelectedAnnouncement(null)}></button>
                        </div>
                        <div className="modal-body">
                            {selectedAnnouncement && (<p>Are you sure you want to delete <strong>{selectedAnnouncement.title}</strong>?</p>)}
                            {deleteMutation.isLoading && <div className="text-center"><div className="spinner-border spinner-border-sm" role="status"><span className="visually-hidden">Loading...</span></div> Deleting...</div>}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" data-bs-dismiss="modal" disabled={deleteMutation.isLoading} onClick={() => setSelectedAnnouncement(null)}>Close</button>
                            <button type="button" className="btn btn-danger"
                                onClick={() => { if (selectedAnnouncement) deleteMutation.mutate(selectedAnnouncement.id); }}
                                disabled={!selectedAnnouncement || deleteMutation.isLoading}
                                data-bs-dismiss={!deleteMutation.isLoading ? "modal" : ""}>
                                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Announcements;